// Authored story copy per sized use case: the "what changes" cell (the same for every customer, no
// numbers) and the business-value phrase (the deck appends the customer's program value to it).
// Deck-safe leaf module: imports nothing, so both the homepage approach table and the executive deck
// (which may not import the engine) can read it.
//
// The three rows of the "Clay's technical wins drive business value" slide in the Value Story Sales
// Enablement deck are the source of the first three entries, put into second person ("you"): CRM enrichment, Signal-based outbound and GTM automation (the deck
// calls it "Prospecting"). Every other entry is DRAFT, written in the same voice and awaiting review
// by the value team.

export interface UseCaseStoryCopy {
  /** What changes for the customer's team. Static: same wording for every customer. */
  change: string;
  /** The business outcome, as a phrase; the deck follows it with "· $X/yr" (program value). */
  value: string;
}

export const USE_CASE_STORY_COPY: Readonly<Record<string, UseCaseStoryCopy>> = {
  // from the enablement deck, in second person
  'uc-crm-enrichment': {
    change: "Your reps stop skipping records they can't work, and your routing finally fires on complete data.",
    value: 'Pipeline from accounts that were invisible to you last quarter',
  },
  'uc-signal-based-outbound': {
    change: 'Your outreach lands with a reason attached, so reply and meeting rates move.',
    value: 'More qualified pipeline at your current headcount',
  },
  'uc-gtm-automation': {
    change: 'Your same team covers more territory without adding an SDR.',
    value: "Fully-loaded cost saved, in your budget this year",
  },

  // DRAFT: for review
  'uc-account-lead-scoring': {
    change: 'Your reps start with the accounts most likely to buy, instead of working a flat list.',
    value: 'Higher win rate on the accounts that matter most to you',
  },
  'uc-tam-sourcing': {
    change: 'Every account that fits your ICP is in your CRM, so you stop selling only to the names you already know.',
    value: 'Pipeline from accounts nobody on your team was working before',
  },
  'uc-inbound-speed-to-lead': {
    change: 'Every inbound lead is matched and routed in minutes, so your reps reach out while the lead is still warm.',
    value: 'More inbound leads converting into pipeline',
  },
  'uc-automated-outbound': {
    change: 'Your sequences start from researched, accurate lists, so outreach runs without manual list building in the way.',
    value: 'Pipeline from outreach you could not run by hand',
  },
  'uc-performance-marketing': {
    change: 'Your ad audiences are built from matched CRM and Clay data, so less of your budget reaches people who do not fit.',
    value: 'Ad spend saved on audiences that never matched',
  },
  'uc-rep-prospecting': {
    change: 'Your reps get researched, ready-to-send prospects instead of building lists themselves.',
    value: 'Pipeline from the hours your reps get back for selling',
  },
  'uc-plg-expansion': {
    change: 'Product usage flags the accounts ready to expand, and your reps reach them first.',
    value: 'Expansion pipeline from accounts already using your product',
  },
  'uc-abm': {
    change: 'Your target accounts are enrolled with the right contacts and signals, so campaigns reach the whole buying group.',
    value: 'Higher win rate on the accounts worked as ABM',
  },
  'uc-tool-consolidation': {
    change: 'Your enrichment and data tools collapse into one place, and the overlapping contracts are cancelled.',
    value: "Vendor spend cancelled, in your budget this year",
  },
};

export function useCaseStoryCopy(useCaseId: string): UseCaseStoryCopy | null {
  return USE_CASE_STORY_COPY[useCaseId] ?? null;
}
